import type { CreatorEntity, LeaderboardData } from "shared/schema";
import { Icon, type ListProps, Table } from "shared/react";
import { formatTaskAmount, getLeaderboardID } from "shared/schema";
import { ProfileButton } from "../entity/EntityButton.js";
import { useItem, useQuery } from "../util/db.js";
import { USER } from "../util/user.js";

/**
 * Show a set of placements for a leaderboard for a given challenge task.
 * - Shows top 5 placements for a leaderboard.
 * - Shows your placement in the leaderboard (if you've either slotted in to the top 5 placements
 */
export function LeaderboardTable({ creator, cid, tid, limit = 10, ...props }: { creator: CreatorEntity; cid: string; tid: string; limit?: number | undefined } & ListProps): React.ReactElement {
	const uid = USER.data.uid;
	const store = useQuery("leaderboard", { creator, cid, tid, $order: "!total", $limit: limit });
	return (
		<Table //
			store={store}
			mapper={(data, i) => <RawLeaderboardRow key={data.uid} {...data} place={i + 1} highlight={data.uid === uid} />}
			empty="No leaderboard activity"
			after={store.value.find(data => data.uid === uid) ? null : <UserLeaderboardRow creator={creator} cid={cid} tid={tid} uid={uid} />}
			{...props}
		/>
	);
}

/** Show the logged in user's flex-row in a leaderboard table. */
function UserLeaderboardRow({ creator, cid, tid, uid }: { creator: CreatorEntity; cid: string; tid: string; uid: string }): React.ReactElement | null {
	const placement = useItem("leaderboard", uid ? getLeaderboardID(creator, cid, tid, uid) : undefined)?.value;
	return placement ? <RawLeaderboardRow {...placement} /> : null;
}

function RawLeaderboardRow({ total, type, units, highlight = false, uid, place }: LeaderboardData & { place?: number; highlight?: boolean }): React.ReactElement {
	return (
		<tr className={highlight ? "fill-panel" : undefined}>
			<th className="width-fit">
				<Icon icon="challenge" mods={`size-2x ${place === 1 ? "color-gold" : place === 2 ? "color-silver" : place === 3 ? "color-bronze" : "color-quiet"}`} />
			</th>
			<th className="width-fit">{place}</th>
			<td>
				<ProfileButton uid={uid} mods="size-small" />
			</td>
			<td className="width-fit text-right text-nowrap">{formatTaskAmount(total, type, units)}</td>
		</tr>
	);
}
